import React, { useEffect, useRef, useState } from "react";
import {
  Captions,
  CaptionsOff,
  Minimize2,
  LoaderCircle,
  PauseIcon,
  PlayIcon,
  RotateCcwIcon,
  SkipForwardIcon,
  Volume1,
  VolumeX,
  ListTodoIcon,
} from "lucide-react";
import { observer } from "mobx-react-lite";
import { useHover } from "usehooks-ts";
import { AUDIO_ONLY_AVATAR_IMG, AvatarMediaError } from "@/lib/tour/tourPlayback";
import { store } from "@/store";
import AvatarButton from "./AvatarButton";
import "./Avatar.css";
import { AvatarDragOverlay } from "./AvatarDragOverlay";
import { debugLog } from "@/lib/log";
import { AvatarTooltip } from "./AvatarTooltip";
import { debounce } from "lodash-es";
import { action } from "mobx";
import { ElementPointer } from "./element-higlighter/ElementPointer";
import { VirtualCursor } from "./VirtualCursor";
import { FloatingPortal } from "@floating-ui/react";

import logo from "@/assets/logo-without-brand.svg";

interface AvatarVideoProps {
  ref: React.MutableRefObject<HTMLVideoElement | null>;
  autoPlay: boolean;
  muted: boolean;
  onPlay: () => void;
  onPause: () => void;
  onCanPlay?: (e: React.SyntheticEvent<HTMLMediaElement, Event>) => void;
  onCanPlayThrough?: (e: React.SyntheticEvent<HTMLMediaElement, Event>) => void;
  onPlaying?: (e: React.SyntheticEvent<HTMLMediaElement, Event>) => void;
  onClick: () => void;
  onEnded: () => void;
}

export interface AvatarProps {
  videoSrc: string;
  nextVideoSrc: string;
  initializedAt: number;
  previewImage: string;
  previewGifUrl: string;
  highlightPlayButton?: boolean;
  progressRenderer: (width: number) => React.ReactNode;
  onPlay?: () => void;
  onCanPlay?: (e: React.SyntheticEvent<HTMLMediaElement, Event>) => void;
  onCanPlayThrough?: (e: React.SyntheticEvent<HTMLMediaElement, Event>) => void;
  onPlaying?: (e: React.SyntheticEvent<HTMLMediaElement, Event>) => void;
  onPause?: () => void;
  onFinish?: () => void;
  onError?: (error: AvatarMediaError) => void;
  isBuffering?: boolean;
  isMuted: boolean;
  isCaptionsVisible: boolean;
  isChecklistButtonVisible?: boolean;
  onChecklistButtonClicked?: () => void;
  onMuteChange: (isMuted: boolean) => void;
  onShowCaptionsChange: (isVisible: boolean) => void;
  onMediaElementRef?: (el: HTMLMediaElement) => void;
  isAudioOnly?: boolean;
  isFeedbackButtonVisible?: boolean;
  isProgressRingVisible?: boolean;
  isControlsAlwaysVisible?: boolean;
  canPlay: boolean;
  canPause: boolean;
  canSkip: boolean;
  canReplay: boolean;
  onPlayClicked?: () => void;
  onPauseClicked?: () => void;
  onSkipClicked?: () => void;
  onReplayClicked?: () => void;
  onMinimizeClicked?: () => void;
  onGiveFeedbackClicked?: () => void;
}

export const Avatar = observer(function Avatar({
  videoSrc,
  nextVideoSrc,
  initializedAt,
  previewImage,
  previewGifUrl,
  highlightPlayButton = false,
  isMuted,
  isCaptionsVisible,
  isFeedbackButtonVisible = true,
  onFinish = () => {},
  onPlay = () => {},
  onCanPlay = () => {},
  onCanPlayThrough = () => {},
  onPlaying = () => {},
  onPause = () => {},
  onError,
  onMediaElementRef,
  progressRenderer,
  isAudioOnly,
  isProgressRingVisible = true,
  isControlsAlwaysVisible = false,
  isChecklistButtonVisible = true,
  onChecklistButtonClicked = () => {},
  isBuffering = false,
  canPlay = false,
  canPause = false,
  canSkip = false,
  canReplay = false,
  onMuteChange,
  onShowCaptionsChange,
  onPlayClicked = () => {},
  onPauseClicked = () => {},
  onSkipClicked = () => {},
  onReplayClicked = () => {},
  onMinimizeClicked = () => {},
  onGiveFeedbackClicked = () => {},
}: AvatarProps) {
  const [isBufferingState, setIsBufferingState] = useState(isBuffering);
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const videoControlsRef = useRef<HTMLDivElement | null>(null);
  const playButtonRef = useRef<HTMLButtonElement | null>(null);
  const avatarContainerRef = useRef<HTMLDivElement | null>(null);
  const isAvatarContainerHover = useHover(avatarContainerRef);
  const isVideoControlsRefHover = useHover(videoControlsRef);
  const [thumbnailImage, setThumbnailImage] = useState<string>("");
  const [showVirtualCursor, setShowVirtualCursor] = useState(false);

  const debouncedSetBuffering = useRef(
    debounce(
      action((buffering: boolean) => {
        setIsBufferingState(buffering);
      }),
      400
    )
  ).current;

  useEffect(() => {
    debouncedSetBuffering(isBuffering);
  }, [isBuffering, debouncedSetBuffering]);

  const canDoAnything = canPlay || canPause || canSkip || canReplay || !isBuffering;
  const isControlsVisible =
    (canDoAnything && (isAvatarContainerHover || isVideoControlsRefHover || isControlsAlwaysVisible)) ||
    isBufferingState;

  useEffect(() => {
    const image = new Image();
    image.src = previewImage;
    image.onload = () => {
      setThumbnailImage(image.src);
    };
    image.onerror = (error) => {
      setThumbnailImage("");
    };

    console.log("Preview image loaded", previewImage);
  }, [previewImage]);

  const videoContainerRef = React.useRef<HTMLDivElement>(null);
  store.avatarPosition.updateElement(videoContainerRef.current ?? null);
  const avatarWidth = store.avatarPosition.avatarWidth;

  const shouldHighlightPlayButton = !isBufferingState && canPlay && highlightPlayButton && !!playButtonRef.current;

  useEffect(() => {
    if (shouldHighlightPlayButton) {
      const timer = setTimeout(() => {
        setShowVirtualCursor(true);
      }, 1500);
      return () => clearTimeout(timer);
    } else {
      setShowVirtualCursor(false);
    }
  }, [shouldHighlightPlayButton]);

  return (
    <>
      <AvatarDragOverlay />
      <div
        id="pyne-avatar"
        ref={videoContainerRef}
        data-controls-visible={isControlsVisible ? "true" : "false"}
        style={{
          backgroundImage: thumbnailImage ? `url(${thumbnailImage})` : undefined,
          backgroundSize: thumbnailImage ? "cover" : undefined,
          backgroundPosition: thumbnailImage ? "center" : undefined,
          backgroundRepeat: thumbnailImage ? "no-repeat" : undefined,
          cursor: canPlay || canPause || canReplay ? "pointer" : "default",
        }}
      >
        <AvatarVideo
          avatarWidth={avatarWidth}
          videoSrc={videoSrc}
          nextVideoSrc={nextVideoSrc}
          initializedAt={initializedAt}
          containerRef={avatarContainerRef}
          onMediaElementRef={onMediaElementRef}
          previewGifUrl={previewGifUrl}
          showPreviewGif={highlightPlayButton}
          onError={onError}
          parentHasPreviewImage={!!thumbnailImage}
          videoProps={{
            ref: videoRef,
            autoPlay: false,
            onPlay: () => {
              onPlay();
            },
            onPause,
            onPlaying: (e) => {
              onPlaying(e);
              // Clear preview image when video starts playing (handles both manual and auto-play)
              setThumbnailImage("");
            },
            onCanPlay,
            onCanPlayThrough,
            onClick: () => {
              if (canPlay) {
                onPlayClicked();
              } else if (canPause) {
                onPauseClicked();
              } else if (canReplay) {
                onReplayClicked();
              }
            },
            muted: isMuted,
            onEnded: onFinish,
          }}
          isAudioOnly={isAudioOnly}
        />
        {isProgressRingVisible && progressRenderer(avatarWidth)}

        <AvatarButton
          className="pyne-avatar-btn__dismiss"
          icon={<Minimize2 />}
          onClick={onMinimizeClicked}
          title="Minimize"
        />
        {isChecklistButtonVisible && (
          <AvatarButton
            className="pyne-avatar-btn__checklist"
            icon={<ListTodoIcon />}
            onClick={onChecklistButtonClicked}
            title="Checklist"
          />
        )}

        <div className="pyne-avatar-waiting">Waiting&hellip;</div>

        <div className="pyne-video__controls" ref={videoControlsRef}>
          <div style={{ position: "relative", width: "32px" }}></div>
          {!isBufferingState && canPlay && (
            <PlayButton ref={playButtonRef} onClick={onPlayClicked} avatarWidth={avatarWidth} />
          )}
          {shouldHighlightPlayButton && (
            <>
              <ElementPointer
                id="pyne-avatar-pointer"
                target={playButtonRef.current!}
                placement="top-start"
                offset={{ mainAxis: 5, crossAxis: 9 }}
                scale={1.8}
              />
            </>
          )}
          {showVirtualCursor && (
            <FloatingPortal>
              <VirtualCursor
                id="pyne-avatar-cursor"
                target={playButtonRef.current}
                visible={showVirtualCursor}
                click="move"
                startPosition={{ x: window.innerWidth / 2, y: window.innerHeight / 2 }}
                shiftOffset={{ top: "-4px", left: "-7px" }}
                travelDuration={1000}
              />
            </FloatingPortal>
          )}

          {!isBufferingState && canPause && <PauseButton onClick={onPauseClicked} avatarWidth={avatarWidth} />}
          {!isBufferingState && canReplay && <ReplayButton onClick={onReplayClicked} avatarWidth={avatarWidth} />}
          {isBufferingState && <BufferingIndicator avatarWidth={avatarWidth} />}
          {!isBufferingState && canSkip ? (
            <AvatarTooltip content="Skip" tooltipProps={{ placement: "top" }}>
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  onSkipClicked?.();
                }}
                className="pyne-avatar-btn__skip"
                title="Skip"
              >
                <SkipForwardIcon
                  style={{
                    width: avatarWidth / 8,
                    height: avatarWidth / 8,
                  }}
                />
              </button>
            </AvatarTooltip>
          ) : (
            <div style={{ position: "relative", width: "32px" }}></div>
          )}
        </div>

        <div className="pyne-avatar__secondary-controls">
          {isCaptionsVisible ? (
            <AvatarButton
              icon={<Captions />}
              onClick={() => onShowCaptionsChange?.(!isCaptionsVisible)}
              title="Hide captions"
            />
          ) : (
            <AvatarButton
              icon={<CaptionsOff />}
              onClick={() => onShowCaptionsChange?.(!isCaptionsVisible)}
              title="Show captions"
            />
          )}
          {isMuted ? (
            <AvatarButton icon={<VolumeX />} onClick={() => onMuteChange?.(!isMuted)} title="Unmute" />
          ) : (
            <AvatarButton icon={<Volume1 />} onClick={() => onMuteChange?.(!isMuted)} title="Mute" />
          )}
          {isFeedbackButtonVisible && (
            <AvatarButton
              icon={<img src={logo} alt="Logo" style={{ width: "80%", height: "80%" }} />}
              onClick={() => {
                onGiveFeedbackClicked();
              }}
              title="Give feedback"
            />
          )}
        </div>
      </div>
    </>
  );
});
export function AvatarVideo({
  videoSrc,
  nextVideoSrc,
  containerRef,
  videoProps,
  avatarWidth,
  onMediaElementRef,
  isAudioOnly,
  initializedAt,
  previewGifUrl,
  onError,
  showPreviewGif = false,
  parentHasPreviewImage = false,
}: {
  initializedAt: number;
  avatarWidth: number;
  nextVideoSrc: string;
  videoSrc: string;
  containerRef: React.RefObject<HTMLDivElement>;
  videoProps?: AvatarVideoProps;
  onMediaElementRef?: (el: HTMLVideoElement) => void;
  isAudioOnly?: boolean;
  previewGifUrl?: string;
  showPreviewGif?: boolean;
  showPulsingDotOnPlay?: boolean;
  onError?: (error: AvatarMediaError) => void;
  parentHasPreviewImage?: boolean;
}) {
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const videoSizingStyle: React.CSSProperties = {
    width: avatarWidth + "px",
    height: avatarWidth + "px",
    position: "absolute",
    transform: "none",
  };

  useEffect(() => {
    return () => {
      if (videoRef.current) {
        // Unload video when component unmounts. This is necessary to prevent media keys from controlling
        // the video even after the component is removed from the DOM.
        videoRef.current.src = "";
        videoRef.current.load();
      }
    };
  }, []);

  let audioOnlyStyles = undefined;
  if (isAudioOnly && !parentHasPreviewImage) {
    audioOnlyStyles = {
      backgroundImage: `url(${AUDIO_ONLY_AVATAR_IMG})`,
      backgroundSize: "cover",
      backgroundPosition: "center",
    };
  }

  let previewGifStyles = undefined;
  if (showPreviewGif && !parentHasPreviewImage) {
    previewGifStyles = {
      backgroundImage: `url(${previewGifUrl})`,
      backgroundSize: "cover",
      backgroundPosition: "center",
    };
  }

  return (
    <>
      <div id="pyne-avatar-video-container" ref={containerRef} style={{ ...audioOnlyStyles, ...previewGifStyles }}>
        {videoSrc && videoSrc !== "" && (
          <video
            key={`${videoSrc}-${initializedAt}`}
            src={videoSrc}
            {...videoProps}
            onCanPlay={(e) => {
              videoProps?.onCanPlay?.(e);
            }}
            onError={(e) => {
              const videoElement = e.currentTarget as HTMLVideoElement;
              if (videoElement.error) {
                const error: AvatarMediaError = {
                  mediaSource: "current",
                  code: videoElement.error.code,
                  message: videoElement.error.message,
                };
                onError?.(error);
              } else {
                const error: AvatarMediaError = {
                  mediaSource: "current",
                  code: -1,
                  message: `Unknown error: ${e.toString()}`,
                };
                onError?.(error);
              }
            }}
            style={{
              ...videoSizingStyle,
              zIndex: parentHasPreviewImage ? 2 : 10,
              visibility: showPreviewGif ? "hidden" : "visible",
              // Hide video when parent has custom preview image
              opacity: parentHasPreviewImage ? 0 : 1,
            }}
            preload="auto"
            crossOrigin="anonymous"
            ref={(el) => {
              if (!el) return;

              if (videoProps?.ref) {
                (videoProps.ref as any).current = el;
              }

              if (el !== videoRef.current) {
                onMediaElementRef?.(el);
              }

              videoRef.current = el;
            }}
          />
        )}
        {nextVideoSrc && nextVideoSrc !== "" && (
          <video
            key={`${nextVideoSrc}-${initializedAt}`}
            src={nextVideoSrc}
            onError={(e) => {
              const videoElement = e.currentTarget as HTMLVideoElement;
              if (videoElement.error) {
                const error: AvatarMediaError = {
                  mediaSource: "next",
                  code: videoElement.error.code,
                  message: videoElement.error.message,
                };
                onError?.(error);
              } else {
                const error: AvatarMediaError = {
                  mediaSource: "next",
                  code: -1,
                  message: `Unknown error: ${e.toString()}`,
                };
                onError?.(error);
              }
            }}
            muted
            style={{
              ...videoSizingStyle,
              zIndex: parentHasPreviewImage ? 1 : 9,
              visibility: showPreviewGif ? "hidden" : "visible",
              // Hide video when parent has custom preview image
              opacity: parentHasPreviewImage ? 0 : 1,
            }}
            preload="auto"
            crossOrigin="anonymous"
          />
        )}
      </div>
    </>
  );
}

interface PlayButtonProps {
  onClick: () => void;
  avatarWidth: number;
}

const PlayButton = React.forwardRef<HTMLButtonElement, PlayButtonProps>(({ onClick, avatarWidth }, ref) => {
  return (
    <AvatarTooltip content="Play" tooltipProps={{ placement: "top" }}>
      <button ref={ref} onClick={onClick} className="pyne-avatar-btn__play">
        <PlayIcon
          style={{
            width: avatarWidth * 0.2,
            height: avatarWidth * 0.2,
          }}
        />
      </button>
    </AvatarTooltip>
  );
});

const PauseButton = ({ onClick, avatarWidth }: { onClick: () => void; avatarWidth: number }) => {
  return (
    <AvatarTooltip content="Pause" tooltipProps={{ placement: "top" }}>
      <button onClick={onClick} className="pyne-avatar-btn__pause">
        <PauseIcon
          style={{
            width: avatarWidth * 0.2,
            height: avatarWidth * 0.2,
          }}
        />
      </button>
    </AvatarTooltip>
  );
};

const ReplayButton = ({ onClick, avatarWidth }: { onClick: () => void; avatarWidth: number }) => {
  return (
    <AvatarTooltip content="Replay" tooltipProps={{ placement: "top" }}>
      <button onClick={onClick} className="pyne-avatar-btn__replay">
        <RotateCcwIcon
          style={{
            width: avatarWidth * 0.2,
            height: avatarWidth * 0.2,
          }}
        />
      </button>
    </AvatarTooltip>
  );
};

const BufferingIndicator = ({ avatarWidth }: { avatarWidth: number }) => {
  return (
    <div style={{ display: "flex", justifyItems: "center", alignItems: "center" }}>
      <LoaderCircle
        className="pyne-avatar-buffering-icon"
        z={99}
        color="white"
        style={{
          width: avatarWidth * 0.2,
          height: avatarWidth * 0.2,
        }}
      />
    </div>
  );
};
